import { BrowserRouter, HashRouter, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { SiteHeader } from "./components";
import Footer from "./components/Footer";
import SearchOverlay from "./components/SearchOverlay";
import AddToCartToast from "./components/AddToCartToast";
import OrderSheet from "./pages/OrderSheet";
import OrderReview from "./pages/OrderReview";
import Articles from "./pages/Articles";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Affiliates from "./pages/Affiliates";
import ShippingInformation from "./pages/ShippingInformation";
import Contact from "./pages/Contact";
import { OrderDraftProvider } from "./context/OrderDraftContext";
import { useCurrency } from "./context/CurrencyContext";
import { navItems, categoryBanners } from "./data";
import { config } from "./config";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider, useCart } from "./context/CartContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { SiteModeProvider, useSiteMode } from "./context/SiteModeContext";
import { page as trackPage } from "./lib/analytics";
import Home from "./pages/Home";
import CategoryPage from "./pages/CategoryPage";
import CategoryListing from "./pages/CategoryListing";
import ItemListPage from "./pages/ItemListPage";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Quotes from "./pages/Quotes";
import Quote from "./pages/Quote";
import Orders from "./pages/Orders";
import Order from "./pages/Order";
import Account from "./pages/Account";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Checkout from "./pages/Checkout";

const Router = config.isEmbedded ? HashRouter : BrowserRouter;

function PageTracker() {
  const location = useLocation();
  useEffect(() => { trackPage(location.pathname, document.title); }, [location.pathname]);
  return null;
}

// Scroll the viewport to the top whenever the route path changes so
// PDPs (and every other page) open at the top of their content
// instead of inheriting the previous page's scroll position. Mobile
// Safari is the worst offender — without this it'll often leave the
// new page focused mid-scroll.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname]);
  return null;
}

function HeaderWithSession({ onSearch }) {
  const { user } = useAuth();
  const { count } = useCart();
  const { isPublic, isB2B, crossSiteUrl } = useSiteMode();
  const utility = [];
  utility.push({ label: "Search", onClick: onSearch });
  if (isB2B) utility.push({ label: "Order Sheet", path: "/order-sheet" });
  utility.push({ label: `Cart${count ? ` (${count})` : ""}`, path: "/cart" });
  // Cross-site link: B2B side surfaces the public store; public surfaces B2B login.
  if (crossSiteUrl) {
    utility.push({ label: isB2B ? "Public Site" : "B2B Login", href: crossSiteUrl });
  }
  utility.push(
    user
      ? { label: user.firstName ? user.firstName : "Account", path: "/account" }
      : { label: isPublic ? "Sign In" : "Login", path: "/login" }
  );
  return (
    <SiteHeader
      logo={<img src="/mystery-ranch-logo.svg" alt="Mystery Ranch" />}
      navItems={navItems}
      utilityItems={utility}
      onSearch={onSearch}
    />
  );
}

function GlobalCartToast() {
  const { toast, dismissToast } = useCart();
  const { formatAmount } = useCurrency();
  return (
    <AddToCartToast
      open={!!toast}
      item={toast}
      onClose={dismissToast}
      formatPrice={(n) => formatAmount(Number(n) || 0)}
    />
  );
}

function Shell() {
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <>
      <ScrollToTop />
      <PageTracker />
      <HeaderWithSession onSearch={() => setSearchOpen(true)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <GlobalCartToast />
      <main className="app-main">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/military" element={<CategoryListing title="Military" description="Battle-tested carry systems built for the field." image={categoryBanners.military} productLine="military" />} />
        <Route path="/fire" element={<CategoryListing title="Fire" description="Gear ready for heat, grit, and long shifts." image={categoryBanners.fire} productLine="fire" />} />
        <Route path="/hunting" element={<CategoryListing title="Hunting" description="Quiet frames and steady packs for the backcountry." image={categoryBanners.hunting} productLine="hunting" />} />
        <Route path="/everyday" element={<CategoryListing title="Everyday Carry" description="Daily essentials with rugged DNA." image={categoryBanners.everyday} productLine="everyday" />} />
        <Route path="/accessories" element={<CategoryListing title="Accessories" description="Add-ons that extend your kit." image={categoryBanners.accessories} productLine="accessories" />} />
        <Route path="/logo-wear" element={<CategoryListing title="Logo Wear" description="Tees, hats, and soft goods that wear our colors." image={categoryBanners.logoWear} productLine="logo-wear" />} />
        <Route path="/top-sellers" element={<CategoryPage title="Top Sellers" description="The most trusted packs in the lineup." image={categoryBanners.topSellers} chipBehindText />} />
        <Route path="/collections" element={<CategoryPage title="Collections" description="Curated kits and cross-line capsule drops." image={categoryBanners.collections} />} />
        <Route path="/seasonal-offers" element={<CategoryPage title="Seasonal Offers" description="Limited-time pricing for B2B accounts." image={categoryBanners.seasonalOffers} />} />
        <Route path="/stories" element={<CategoryPage title="Stories" description="Field notes, guides, and real-world carry." image={categoryBanners.stories} />} />
        <Route path="/items" element={<ItemListPage />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/order-sheet" element={<OrderSheet />} />
        <Route path="/order-review" element={<OrderReview />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<Articles />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/affiliates" element={<Affiliates />} />
        <Route path="/shipping-information" element={<ShippingInformation />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/quotes/:id" element={<Quote />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<Order />} />
        <Route path="/account" element={<Account />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/checkout" element={<Checkout />} />
      </Routes>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <SiteModeProvider>
        <CurrencyProvider>
        <CartProvider>
        <OrderDraftProvider>
          <Shell />
        </OrderDraftProvider>
        </CartProvider>
        </CurrencyProvider>
        </SiteModeProvider>
      </AuthProvider>
    </Router>
  );
}

